import React from 'react';

import { Account, AccountGroup, GUID, LifecycleStatus } from '@ndvr/portfolio-api';

import { BaseCard } from '@/components/BaseCard';
import { CopiableGuid } from '@/components/CopiableGuid';
import { StatusBadge } from '@/components/StatusBadge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAccountGroupProfileWithAumIncluded } from '@/views/portfolio/account-aum-queries';
import { useUpdateTaxLossHarvesting } from '@/views/portfolio/account-queries';
import { useRequiredParams } from 'utils';

import { supportsTaxLossHarvesting, TAX_LOSS_HARVESTING } from './constants';

const AccountTaxLossHarvestingRow: React.FC<{ account: Account; accountGroupGUID: GUID<AccountGroup> }> = ({
    account,
    accountGroupGUID,
}) => {
    const { mutate: updateTaxLossHarvesting, isPending } = useUpdateTaxLossHarvesting(
        account.accountGUID,
        accountGroupGUID,
    );
    const supported = supportsTaxLossHarvesting(account);
    const switchId = `${TAX_LOSS_HARVESTING.key}-${account.accountGUID}`;

    const control = (
        <Switch
            id={switchId}
            checked={account.taxLossHarvesting}
            disabled={!supported || isPending}
            onCheckedChange={(checked) => updateTaxLossHarvesting({ taxLossHarvesting: checked })}
        />
    );

    return (
        <div className="border-border flex items-center gap-2 border-b px-4 py-3 text-sm">
            <div className="font-semibold text-gray-800">{account.accountName.plainText}</div>
            <StatusBadge status={account.lifecycleStatus} liveDate={account.liveDate} closedAt={account.closedAt} />
            <CopiableGuid className="h-5" guid={account.accountGUID} />
            <Label htmlFor={switchId} className="ml-auto text-sm">
                {TAX_LOSS_HARVESTING.label}
            </Label>
            {supported ? (
                control
            ) : (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span>{control}</span>
                    </TooltipTrigger>
                    <TooltipContent side="top" align="center">
                        Only taxable accounts support tax loss harvesting
                    </TooltipContent>
                </Tooltip>
            )}
        </div>
    );
};

interface AccountLevelCardProps {
    className?: string;
}

/** Per-account tax loss harvesting switches; non-taxable accounts are always off. */
export const AccountLevelInvestmentStyleOverridesCard: React.FC<AccountLevelCardProps> = ({ className }) => {
    const { accountGroupGUID } = useRequiredParams(['accountGroupGUID']);
    const { data: accountGroup } = useAccountGroupProfileWithAumIncluded(accountGroupGUID);

    const statusOrder: Record<LifecycleStatus, number> = {
        [LifecycleStatus.REAL]: 0,
        [LifecycleStatus.DRAFT]: 1,
        [LifecycleStatus.ONBOARDING]: 2,
        [LifecycleStatus.CLOSED]: 3,
    };
    const sortedAccounts = accountGroup?.accounts
        ?.slice()
        .sort((a: Account, b: Account) => statusOrder[a.lifecycleStatus] - statusOrder[b.lifecycleStatus]);

    return (
        <BaseCard className={className} title="Account Level Investment Styles">
            {sortedAccounts?.map((account: Account) => (
                <AccountTaxLossHarvestingRow
                    key={account.accountGUID}
                    account={account}
                    accountGroupGUID={accountGroupGUID}
                />
            ))}
        </BaseCard>
    );
};
